import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Shield, Mail, Lock, Eye, EyeOff, ArrowRight, User, Building, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login', 'register', 'forgot'
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    organization: '',
    phone: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === 'login') {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        if (response.data.success) {
          // Store token and user data
          localStorage.setItem('nikosafe_token', response.data.token);
          localStorage.setItem('nikosafe_user', JSON.stringify(response.data.user));
          
          toast.success('Welcome back! Redirecting to dashboard...');
          onClose();
          
          // Redirect based on role
          const userRole = response.data.user?.role;
          setTimeout(() => {
            if (userRole === 'OWNER') {
              navigate('/owner');
            } else {
              // HOSPITAL_ADMIN and STAFF go to hospital dashboard
              navigate('/hospital');
            }
            if (onLoginSuccess) onLoginSuccess(response.data.user);
          }, 500);
        }
      } else if (mode === 'register') {
        // Validate passwords match
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setIsLoading(false);
          return;
        }
        
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setIsLoading(false);
          return;
        }
        
        const response = await axios.post(`${API}/auth/register`, {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          organization: formData.organization,
          phone: formData.phone
        });
        
        if (response.data.success) {
          // Store token and user data
          localStorage.setItem('nikosafe_token', response.data.token);
          localStorage.setItem('nikosafe_user', JSON.stringify(response.data.user));
          
          toast.success('Account created successfully!');
          onClose();
          
          setTimeout(() => {
            navigate('/hospital');
            if (onLoginSuccess) onLoginSuccess(response.data.user);
          }, 500);
        }
      } else {
        // Forgot password - mock for now
        toast.success('Password reset link sent to your email.');
        setMode('login');
      }
    } catch (error) {
      console.error('Auth error:', error);
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-gray-100 rounded-lg transition-colors z-10"
          data-testid="login-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="relative px-8 pt-8 pb-6 bg-gradient-to-br from-[var(--niko-teal-dark)] to-[var(--niko-teal)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">NikoSafe Connect</h2>
              <p className="text-sm text-white/70">Healthcare Safety Platform</p>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-white">
            {mode === 'login' && 'Welcome Back'}
            {mode === 'register' && 'Create Your Account'}
            {mode === 'forgot' && 'Reset Password'}
          </h3>
          <p className="text-sm text-white/70 mt-1">
            {mode === 'login' && 'Sign in to access your safety dashboard'}
            {mode === 'register' && 'Join healthcare teams using NikoSafe Connect'}
            {mode === 'forgot' && "We'll send you a reset link"}
          </p>
        </div>

        {/* Demo Account Notice */}
        {mode === 'login' && (
          <div className="mx-8 mt-4 p-3 bg-[var(--accent-wash)] rounded-lg border border-[var(--niko-teal)]/20">
            <p className="text-xs text-[var(--text-secondary)] mb-1">
              <strong className="text-[var(--niko-teal)]">Demo Accounts:</strong>
            </p>
            <p className="text-xs text-[var(--text-muted)]">
              Owner: <code className="bg-gray-100 px-1 rounded">demo@nikosafe.com</code> / <code className="bg-gray-100 px-1 rounded">demo123</code>
            </p>
            <p className="text-xs text-[var(--text-muted)]">
              Hospital: <code className="bg-gray-100 px-1 rounded">hospital@nikosafe.com</code> / <code className="bg-gray-100 px-1 rounded">hospital123</code>
            </p>
          </div>
        )}

        {/* Form */}
        <div className="p-8 pt-4">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {mode === 'register' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[var(--text-muted)]" />
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jane Smith, RN"
                    required
                    className="h-12"
                    data-testid="register-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organization" className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-[var(--text-muted)]" />
                    Organization
                  </Label>
                  <Input
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder="Hospital or Facility Name"
                    className="h-12"
                    data-testid="register-organization-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[var(--text-muted)]" />
                    Phone (Optional)
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="h-12"
                    data-testid="register-phone-input"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[var(--text-muted)]" />
                Work Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@hospital.org"
                required
                className="h-12"
                data-testid="email-input"
              />
            </div>

            {mode !== 'forgot' && (
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[var(--text-muted)]" />
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className="h-12 pr-12"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {mode === 'register' && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[var(--text-muted)]" />
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  className="h-12"
                  data-testid="confirm-password-input"
                />
              </div>
            )}

            {mode === 'login' && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  className="text-sm text-[var(--niko-teal)] hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary h-12 text-base mt-2"
              data-testid="submit-button"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {mode === 'login' && 'Sign In'}
                  {mode === 'register' && 'Create Account'}
                  {mode === 'forgot' && 'Send Reset Link'}
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>

          {/* Mode Toggle */}
          <div className="mt-6 pt-6 border-t border-[var(--border-light)] text-center">
            {mode === 'login' && (
              <p className="text-sm text-[var(--text-secondary)]">
                New to NikoSafe Connect?{' '}
                <button
                  onClick={() => setMode('register')}
                  className="text-[var(--niko-teal)] font-medium hover:underline"
                  data-testid="switch-to-register"
                >
                  Create an account
                </button>
              </p>
            )}
            {(mode === 'register' || mode === 'forgot') && (
              <p className="text-sm text-[var(--text-secondary)]">
                Already have an account?{' '}
                <button
                  onClick={() => setMode('login')}
                  className="text-[var(--niko-teal)] font-medium hover:underline"
                  data-testid="switch-to-login"
                >
                  Sign in
                </button>
              </p>
            )}
          </div>

          {/* Security Note */}
          <p className="mt-6 text-xs text-center text-[var(--text-muted)]">
            🔒 HIPAA compliant • 256-bit encryption • SOC 2 certified
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
